import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { PRODUCTS, type ProductId } from '@/config/products';

interface ProductSubscription {
  subscribed: boolean;
  trialing: boolean;
  trialEnd: string | null;
  subscriptionEnd: string | null;
}

interface SubscriptionStatus {
  subscribed: boolean;
  trialing: boolean;
  trialEnd: string | null;
  subscriptionEnd: string | null;
  // Per-product subscription status
  products: Record<ProductId, ProductSubscription>;
}

interface AuthOptions {
  captchaToken?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionStatus;
  subscriptionLoading: boolean;
  signUp: (email: string, password: string, options?: AuthOptions) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string, options?: AuthOptions) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<void>;
  isSubscribedTo: (productId: ProductId) => boolean;
  resetPasswordForEmail: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const defaultProductSubscription: ProductSubscription = {
  subscribed: false,
  trialing: false,
  trialEnd: null,
  subscriptionEnd: null,
};

const getDefaultProductsMap = (): Record<ProductId, ProductSubscription> => {
  const map: Partial<Record<ProductId, ProductSubscription>> = {};
  for (const productId of Object.keys(PRODUCTS) as ProductId[]) {
    map[productId] = { ...defaultProductSubscription };
  }
  return map as Record<ProductId, ProductSubscription>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    subscribed: false,
    trialing: false,
    trialEnd: null,
    subscriptionEnd: null,
    products: getDefaultProductsMap(),
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Helper to check if user is subscribed to a specific product
  const isSubscribedTo = (productId: ProductId): boolean => {
    const productSub = subscription.products[productId];
    return productSub?.subscribed || productSub?.trialing || false;
  };

  const checkSubscription = async () => {
    if (!session) {
      setSubscription({
        subscribed: false,
        trialing: false,
        trialEnd: null,
        subscriptionEnd: null,
        products: getDefaultProductsMap(),
      });
      return;
    }

    setSubscriptionLoading(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession?.access_token) {
        console.log('No valid session token available');
        setSubscriptionLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });
      
      if (error) {
        console.warn('Subscription check failed (non-blocking):', error.message);
        setSubscription(prev => prev.subscribed ? prev : {
          subscribed: false,
          trialing: false,
          trialEnd: null,
          subscriptionEnd: null,
          products: getDefaultProductsMap(),
        });
        setSubscriptionLoading(false);
        return;
      }

      if (data) {
        // Map Stripe product IDs to our ProductIds
        const productsMap = getDefaultProductsMap();
        
        if (data.subscriptions) {
          // Iterate through our products and check if they're in the subscriptions
          for (const [productId, product] of Object.entries(PRODUCTS)) {
            for (const tier of product.tiers) {
              // Check if this product's Stripe product ID is in the subscriptions
              if (data.subscriptions[tier.productId]) {
                const subData = data.subscriptions[tier.productId];
                productsMap[productId as ProductId] = {
                  subscribed: subData.subscribed,
                  trialing: subData.trialing,
                  trialEnd: subData.trial_end,
                  subscriptionEnd: subData.subscription_end,
                };
              }
            }
          }
        }

        setSubscription({
          subscribed: data.subscribed ?? false,
          trialing: data.trialing ?? false,
          trialEnd: data.trial_end ?? null,
          subscriptionEnd: data.subscription_end ?? null,
          products: productsMap,
        });
      }
    } catch (err) {
      console.warn('Subscription check error (non-blocking):', err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  useEffect(() => {
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session) {
          setTimeout(() => {
            checkSubscription();
          }, 0);
        } else {
          setSubscription({
            subscribed: false,
            trialing: false,
            trialEnd: null,
            subscriptionEnd: null,
            products: getDefaultProductsMap(),
          });
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session) {
        setTimeout(() => {
          checkSubscription();
        }, 0);
      }
    });

    return () => authSubscription.unsubscribe();
  }, []);

  // Periodic subscription check every minute
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      checkSubscription();
    }, 60000);

    return () => clearInterval(interval);
  }, [session]);

  const signUp = async (email: string, password: string, options?: AuthOptions) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        captchaToken: options?.captchaToken,
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string, options?: AuthOptions) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        captchaToken: options?.captchaToken,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPasswordForEmail = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        subscription,
        subscriptionLoading,
        signUp,
        signIn,
        signOut,
        checkSubscription,
        isSubscribedTo,
        resetPasswordForEmail,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
